import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const PrivacyPolicy = ({ openModal }) => {
  return (
    <div className="legal-page">
      <header className="legal-hero">
        <div className="legal-hero-inner">
          <nav className="legal-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="bc-separator">/</span>
            <span>Privacy Policy</span>
          </nav>
          <span className="legal-badge">Client Privacy & Data Protection</span>
          <h1>Privacy Policy</h1>
          <p className="legal-hero-lead">
            At Embrace Beauty Aesthetics, protecting your privacy and personal health information is of paramount importance. This policy outlines how we collect, use, store, and safeguard your details.
          </p>
          <div className="legal-meta">
            <div className="legal-meta-item">
              <span>📅 Effective Date: October 2025</span>
            </div>
            <div className="legal-meta-item">
              <span>📍 Embrace Beauty Aesthetics Clinic</span>
            </div>
          </div>
        </div>
      </header>

      <main className="legal-main">
        <div className="legal-container">
          <article className="legal-card">
            <div className="legal-toc">
              <div className="legal-toc-title">Quick Navigation</div>
              <ul className="legal-toc-list">
                <li><a href="#section-1">1. Information We Collect</a></li>
                <li><a href="#section-2">2. How We Use Your Information</a></li>
                <li><a href="#section-3">3. Data Security & Confidentiality</a></li>
                <li><a href="#section-4">4. Sharing & Third-Party Disclosure</a></li>
                <li><a href="#section-5">5. Cookies & Tracking Technologies</a></li>
                <li><a href="#section-6">6. Your Rights & Choices</a></li>
                <li><a href="#section-7">7. Contact Us & Inquiries</a></li>
              </ul>
            </div>

            <section className="legal-section" id="section-1">
              <div className="legal-section-header">
                <span className="legal-section-num">01</span>
                <h2>Information We Collect</h2>
              </div>
              <p>
                When you visit our clinic, browse our website, or interact with our online booking services, we may collect various types of information to serve you better:
              </p>
              <ul className="legal-list">
                <li>
                  <strong>Personal Identification:</strong> Full name, email address, telephone number, mailing address, and date of birth provided during consultations or form submissions.
                </li>
                <li>
                  <strong>Health & Aesthetic Profile:</strong> Skin concerns, medical history, allergies, previous aesthetic treatments, and treatment goals shared during consultations or questionnaires to ensure your treatment is safe and personalized.
                </li>
                <li>
                  <strong>Appointment & Treatment Records:</strong> Booked services, appointment history, practitioner notes, and progress photography (with your explicit consent).
                </li>
                <li>
                  <strong>Technical & Usage Data:</strong> IP address, device type, operating system, browser information, pages viewed, and session metrics collected via standard web analytics.
                </li>
              </ul>
            </section>

            <section className="legal-section" id="section-2">
              <div className="legal-section-header">
                <span className="legal-section-num">02</span>
                <h2>How We Use Your Information</h2>
              </div>
              <p>
                We use the personal information collected only for legitimate healthcare, clinical, and operational purposes:
              </p>
              <ul className="legal-list">
                <li>To schedule, confirm, modify, or remind you about upcoming appointments.</li>
                <li>To formulate tailored treatment plans that accommodate your skin type and health status.</li>
                <li>To send pre-care preparation and post-procedure aftercare instructions.</li>
                <li>To respond to your questions, feedback, and customer support inquiries.</li>
                <li>To inform you about seasonal promotions, new beauty procedures, or clinic announcements (which you can opt out of at any time).</li>
                <li>To maintain medical and legal records as required by aesthetic medicine regulations.</li>
              </ul>
              <div className="legal-callout">
                <h4>🔒 We Never Sell Your Data</h4>
                <p>
                  Embrace Beauty Aesthetics does not sell, rent, or trade your personal information to third-party marketing companies under any circumstances.
                </p>
              </div>
            </section>

            <section className="legal-section" id="section-3">
              <div className="legal-section-header">
                <span className="legal-section-num">03</span>
                <h2>Data Security & Confidentiality</h2>
              </div>
              <p>
                We implement industry-standard administrative, technical, and physical safeguards to protect your personal data against unauthorized access, alteration, disclosure, or destruction.
              </p>
              <p>
                All digital communications and patient intake forms are transmitted via Secure Socket Layer (SSL) encryption. Access to sensitive clinical notes is strictly restricted to authorized aesthetic and medical personnel who require the information to perform their duties.
              </p>
            </section>

            <section className="legal-section" id="section-4">
              <div className="legal-section-header">
                <span className="legal-section-num">04</span>
                <h2>Sharing & Third-Party Disclosure</h2>
              </div>
              <p>
                Your information is kept confidential. We only share details under the following limited conditions:
              </p>
              <ul className="legal-list">
                <li>
                  <strong>Authorized Service Providers:</strong> Trusted third-party technology providers that assist in hosting our website, appointment scheduling systems, or financing applications (such as Cherry Payment Plans). These providers are contractually obligated to keep your data secure and confidential.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> If required by court order, subpoena, government regulation, or applicable law to protect the safety and legal rights of our clients, staff, or clinic.
                </li>
              </ul>
            </section>

            <section className="legal-section" id="section-5">
              <div className="legal-section-header">
                <span className="legal-section-num">05</span>
                <h2>Cookies & Tracking Technologies</h2>
              </div>
              <p>
                Our website uses cookies and similar tracking technologies to improve navigation, analyze visitor traffic, and remember your preferences.
              </p>
              <p>
                You can configure your browser to decline cookies or alert you when a cookie is being sent. However, disabling cookies may cause some features of our website to function suboptimally.
              </p>
            </section>

            <section className="legal-section" id="section-6">
              <div className="legal-section-header">
                <span className="legal-section-num">06</span>
                <h2>Your Rights & Choices</h2>
              </div>
              <p>
                You maintain full control over your personal records:
              </p>
              <ul className="legal-list">
                <li><strong>Access & Correction:</strong> You can request a copy of the personal information we hold or ask us to correct inaccurate records.</li>
                <li><strong>Marketing Opt-Out:</strong> You may unsubscribe from marketing SMS or email communications at any time by following the instructions in the message or contacting us.</li>
                <li><strong>Data Deletion:</strong> You may request the deletion of non-essential personal information, subject to mandatory statutory medical record retention obligations.</li>
              </ul>
            </section>

            <section className="legal-section" id="section-7">
              <div className="legal-section-header">
                <span className="legal-section-num">07</span>
                <h2>Contact Us & Inquiries</h2>
              </div>
              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or how your personal information is handled, please do not hesitate to reach out to us:
              </p>
              <ul className="legal-list">
                <li><strong>Phone:</strong> <a href="tel:+14694341574">+1 469-434-1574</a></li>
                <li><strong>Hours:</strong> 9 AM - 10 PM, Monday - Saturday</li>
                <li><strong>Clinic:</strong> Embrace Beauty Aesthetics</li>
              </ul>
            </section>

            <div className="legal-help-box">
              <h3>Have Questions or Need to Book an Appointment?</h3>
              <p>
                Our team is always here to assist you with transparent communication and personalized beauty care.
              </p>
              <div className="legal-help-actions">
                <button className="btn-primary" onClick={openModal}>Make Appointment</button>
                <a href="tel:+14694341574" className="btn-outline">Call +1 469-434-1574</a>
                <Link to="/" className="btn-outline">Back to Home</Link>
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
